import { useCallback, useContext, useEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import { Userpilot } from 'userpilot'
import { StoresContext } from '@App/contexts'
import useCookieConsent from '@App/common/hooks/use-cookie-consent'
import userPilotUserContext from '@App/helpers/user/user-pilot-user-context'

const USERPILOT_SCRIPT_SELECTOR = 'script[src*="userpilot"][src*="/sdk/"]'

const isUserpilotLoaded = () => Boolean(window.userpilot)

const runWhenUserpilotIsReady = (callback: () => void) => {
    if (isUserpilotLoaded()) {
        callback()
        return
    }

    const script = document.querySelector<HTMLScriptElement>(USERPILOT_SCRIPT_SELECTOR)
    if (!script) {
        return
    }

    script.addEventListener('load', callback, { once: true })
    return () => script.removeEventListener('load', callback)
}

const useUserpilotConsent = () => {
    const location = useLocation()
    const { authStore, userStore } = useContext(StoresContext)
    const userPilotToken = window.APP_CONFIG.USER_PILOT_TOKEN || ''
    const userpilotActive = useRef(false)
    const identifiedUserId = useRef<string | undefined>(undefined)
    const previousLocation = useRef(location)
    const hasStatisticsConsent = useCookieConsent('statistics')
    const isLoggedIn = authStore.isLoggedIn
    const userInfo = userStore.userInfo
    const hasStatisticsConsentRef = useRef(hasStatisticsConsent)
    hasStatisticsConsentRef.current = hasStatisticsConsent

    const deactivateUserpilot = useCallback(() => {
        if (isUserpilotLoaded()) {
            Userpilot.suppress()
            Userpilot.clean()
        }
        userpilotActive.current = false
        identifiedUserId.current = undefined
    }, [])

    useEffect(() => {
        if (!userPilotToken) {
            deactivateUserpilot()
            return
        }

        const syncUserpilotSession = () => {
            if (!hasStatisticsConsentRef.current) {
                deactivateUserpilot()
                return
            }

            Userpilot.unsuppress()
            userpilotActive.current = true

            if (userInfo) {
                const userId = userInfo.exposedId || ''
                if (identifiedUserId.current && identifiedUserId.current !== userId) {
                    Userpilot.clean()
                }
                Userpilot.identify(userId, userPilotUserContext(userInfo))
                identifiedUserId.current = userId
            } else {
                if (identifiedUserId.current) {
                    Userpilot.clean()
                }
                identifiedUserId.current = undefined
            }
            Userpilot.reload()
        }

        if (!hasStatisticsConsent) {
            if (window.userpilotSettings?.token === userPilotToken) {
                return runWhenUserpilotIsReady(syncUserpilotSession)
            }
            return
        }

        if (window.userpilotSettings?.token !== userPilotToken) {
            Userpilot.suppress()
            Userpilot.initialize(userPilotToken)
        }

        return runWhenUserpilotIsReady(syncUserpilotSession)
    }, [deactivateUserpilot, hasStatisticsConsent, isLoggedIn, userInfo, userPilotToken])

    useEffect(
        () => () => {
            deactivateUserpilot()
        },
        [deactivateUserpilot]
    )

    useEffect(() => {
        const locationChanged = previousLocation.current !== location
        previousLocation.current = location

        if (locationChanged && hasStatisticsConsent && userpilotActive.current) {
            Userpilot.reload()
        }
    }, [hasStatisticsConsent, location])
}

export default useUserpilotConsent
